import React from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { AppModal } from '../../../../components/AppModal';
import { formatDisplayHours } from '../../../../utils/employeeTimeEntryHours';
import {
  formatIstRange,
  groupIstByWeek,
  totalWorkHours,
  type IstWeek,
  type LeadProjectTimeEntry,
} from './projectActivityIstHours';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function hoursText(value: number): string {
  return value > 0 ? `${formatDisplayHours(value)} h` : '—';
}

function dayMonth(ymd: string): string {
  const [, m, d] = ymd.split('-');
  return `${d}.${m}.`;
}

function cellTitle(rows: LeadProjectTimeEntry[]): string {
  return rows
    .map((row) => {
      const where = row.activity_id ? row.activity_title || 'Termin' : 'ohne Termin';
      return `${formatIstRange(row.start_time, row.end_time)} · ${where}${row.note ? ` · ${row.note}` : ''}`;
    })
    .join('\n');
}

/** One calendar week as employees × days. A click on a day edits its Ist time. */
export function IstWeekTable({
  week,
  onEdit,
}: {
  week: IstWeek;
  onEdit?: (row: LeadProjectTimeEntry) => void;
}) {
  const hasEntry = (day: string) => week.employees.some((e) => (e.byDate[day]?.length ?? 0) > 0);
  // Weekends only when someone worked then.
  const dayIndexes = WEEKDAYS.map((_, i) => i).filter((i) => i < 5 || hasEntry(week.days[i]!));
  const dayHours = (day: string) => week.employees.reduce((sum, e) => sum + totalWorkHours(e.byDate[day] ?? []), 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[28rem] text-[12px] tabular-nums">
        <thead>
          <tr className="text-app-text-subtle">
            <th className="py-1 pr-2 text-left font-medium">Mitarbeiter</th>
            {dayIndexes.map((i) => (
              <th key={i} className="px-1 py-1 text-right font-medium" title={week.days[i]}>
                {WEEKDAYS[i]} <span className="font-normal">{dayMonth(week.days[i]!)}</span>
              </th>
            ))}
            <th className="py-1 pl-2 text-right font-medium">Σ</th>
          </tr>
        </thead>
        <tbody>
          {week.employees.map((employee) => (
            <tr key={employee.employeeId} className="border-t border-app-border">
              <td className="max-w-[12rem] truncate py-1 pr-2 text-app-text">{employee.name}</td>
              {dayIndexes.map((i) => {
                const rows = employee.byDate[week.days[i]!] ?? [];
                const hours = totalWorkHours(rows);
                const inAppointment = rows.some((row) => row.activity_id);
                const label = rows.length === 0 ? '' : formatDisplayHours(hours) || '0';
                const className = `px-1 py-1 text-right ${inAppointment ? 'text-pipedrive-blue' : 'text-app-text'}`;
                if (rows.length === 0 || !onEdit) {
                  return (
                    <td key={i} className={className} title={rows.length ? cellTitle(rows) : undefined}>
                      {label}
                    </td>
                  );
                }
                return (
                  <td key={i} className={className}>
                    <button
                      type="button"
                      onClick={() => onEdit(rows[0]!)}
                      className="hover:underline"
                      title={`${cellTitle(rows)}\nKlicken zum Bearbeiten`}
                    >
                      {label}
                    </button>
                  </td>
                );
              })}
              <td className="py-1 pl-2 text-right font-medium text-app-text">{formatDisplayHours(employee.hours)}</td>
            </tr>
          ))}
          {week.employees.length > 1 ? (
            <tr className="border-t border-app-border text-app-text-subtle">
              <td className="py-1 pr-2">Summe</td>
              {dayIndexes.map((i) => {
                const hours = dayHours(week.days[i]!);
                return (
                  <td key={i} className="px-1 py-1 text-right">
                    {hours > 0 ? formatDisplayHours(hours) : ''}
                  </td>
                );
              })}
              <td className="py-1 pl-2 text-right font-medium text-app-text">{formatDisplayHours(week.hours)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/** Collapsible weeks (newest first): one summary line each, the table when opened. */
export function IstWeekList({
  weeks,
  onEdit,
  initiallyOpen = 0,
}: {
  weeks: IstWeek[];
  onEdit?: (row: LeadProjectTimeEntry) => void;
  /** Number of newest weeks shown opened. */
  initiallyOpen?: number;
}) {
  const [open, setOpen] = React.useState<Set<string>>(() => new Set(weeks.slice(0, initiallyOpen).map((w) => w.key)));
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <ul className="divide-y divide-app-border">
      {weeks.map((week) => {
        const isOpen = open.has(week.key);
        const workedDays = week.days.filter((day) => week.employees.some((e) => e.byDate[day]?.length));
        const range = workedDays.length
          ? `${dayMonth(workedDays[0]!)}–${dayMonth(workedDays[workedDays.length - 1]!)}`
          : '';
        return (
          <li key={week.key}>
            <button
              type="button"
              onClick={() => toggle(week.key)}
              aria-expanded={isOpen}
              className="flex w-full items-center gap-2 py-2 text-left text-[13px] hover:text-pipedrive-blue"
            >
              {isOpen ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-app-text-subtle" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-app-text-subtle" />
              )}
              <span className="font-medium text-app-text">
                KW{week.isoWeek} {week.isoYear}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] text-app-text-subtle">
                {range}
                {range ? ' · ' : ''}
                {week.employees.length} Mitarbeiter
              </span>
              <span className="shrink-0 tabular-nums text-app-text">{hoursText(week.hours)}</span>
            </button>
            {isOpen ? (
              <div className="pb-3 pl-5">
                <IstWeekTable week={week} onEdit={onEdit} />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

type OverviewFilter = 'all' | 'appointment' | 'project';

/** All Ist times of the project by week — opened from Personalaufwand and the appointment list. */
export function LeadIstHoursOverviewModal({
  open,
  onClose,
  items,
  onEdit,
  initialFilter = 'all',
}: {
  open: boolean;
  onClose: () => void;
  items: LeadProjectTimeEntry[];
  onEdit?: (row: LeadProjectTimeEntry) => void;
  initialFilter?: OverviewFilter;
}) {
  const [filter, setFilter] = React.useState<OverviewFilter>(initialFilter);
  React.useEffect(() => {
    if (open) setFilter(initialFilter);
  }, [open, initialFilter]);

  const filtered = React.useMemo(
    () =>
      items.filter((row) =>
        filter === 'all' ? true : filter === 'appointment' ? Boolean(row.activity_id) : !row.activity_id,
      ),
    [items, filter],
  );
  const weeks = React.useMemo(() => groupIstByWeek(filtered), [filtered]);
  const employeeCount = new Set(filtered.map((row) => row.employee_id)).size;
  const hasBoth = items.some((row) => row.activity_id) && items.some((row) => !row.activity_id);
  const filters: { value: OverviewFilter; label: string }[] = [
    { value: 'all', label: 'Alle' },
    { value: 'appointment', label: 'Mit Termin' },
    { value: 'project', label: 'Ohne Termin' },
  ];

  return (
    <AppModal open={open} onClose={onClose} panelClassName="md:max-w-4xl md:w-full" ariaLabelledBy="ist-overview-title">
      <div className="flex min-h-0 flex-1 flex-col md:max-h-[90vh]">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-app-border px-5 py-4">
          <div className="min-w-0">
            <h2 id="ist-overview-title" className="text-base font-semibold text-app-text">
              Ist-Zeiten
            </h2>
            <p className="mt-0.5 text-[13px] text-app-text-subtle">
              {hoursText(totalWorkHours(filtered))} · {employeeCount} Mitarbeiter · {weeks.length}{' '}
              {weeks.length === 1 ? 'Woche' : 'Wochen'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-app-text-subtle hover:bg-app-surface-muted hover:text-app-text"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {hasBoth ? (
          <div className="flex shrink-0 gap-1 border-b border-app-border px-5 py-2">
            {filters.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium ${
                  filter === f.value
                    ? 'bg-pipedrive-blue text-white'
                    : 'text-app-text-subtle hover:bg-app-surface-muted hover:text-app-text'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-2">
          {weeks.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-app-text-subtle">Keine Ist-Zeiten</p>
          ) : (
            <IstWeekList key={filter} weeks={weeks} onEdit={onEdit} initiallyOpen={2} />
          )}
        </div>
        {hasBoth || onEdit ? (
          <p className="shrink-0 border-t border-app-border px-5 py-2 text-[11px] text-app-text-subtle">
            {[hasBoth ? 'Blau = Zeit auf einem Termin.' : null, onEdit ? 'Tag anklicken zum Bearbeiten.' : null]
              .filter(Boolean)
              .join(' ')}
          </p>
        ) : null}
      </div>
    </AppModal>
  );
}
